/**
 * Form Question Ordering Utility
 * Dynamically orders questions based on whether postcode is available from homepage
 */

import { FormConfiguration, Question } from '../types/formConfig'
import { hasPostcode } from './postcodeStorage'

/**
 * Adjust form configuration based on postcode availability
 * Returns modified config with questions reordered
 */
export const adjustFormForPostcode = (config: FormConfiguration): FormConfiguration => {
  const hasStoredPostcode = hasPostcode()

  // Create a copy of the config
  const adjustedConfig = { ...config }
  const questions = [...config.questions]

  if (hasStoredPostcode) {
    // Postcode available - Address first, then phone
    // Remove postcode question if it exists
    const withoutPostcode = questions.filter(q => q.questionType !== 'postcode')

    // Find address and phone questions
    const addressQ = withoutPostcode.find(q => q.questionType === 'address-select')
    const phoneQ = withoutPostcode.find(q => q.questionType === 'tel')

    // Get all other questions
    const otherQuestions = withoutPostcode.filter(
      q => q.questionType !== 'address-select' && q.questionType !== 'tel'
    )

    // Reorder: address, phone, then others
    const reordered: Question[] = []

    if (addressQ) {
      reordered.push({ ...addressQ, order: 1, postcodeSource: 'localStorage' })
    }

    if (phoneQ) {
      reordered.push({ ...phoneQ, order: 2, partialSubmit: true })
    }

    otherQuestions.forEach((q, index) => {
      reordered.push({ ...q, order: index + 3 })
    })

    adjustedConfig.questions = reordered
  } else {
    // No postcode - Phone first, then postcode, then address
    const phoneQ = questions.find(q => q.questionType === 'tel')
    const postcodeQ = questions.find(q => q.questionType === 'postcode')
    const addressQ = questions.find(q => q.questionType === 'address-select')

    // Get all other questions
    const otherQuestions = questions.filter(
      q => q.questionType !== 'tel' &&
           q.questionType !== 'postcode' &&
           q.questionType !== 'address-select'
    )

    // Reorder: phone, postcode, address, then others
    const reordered: Question[] = []

    if (phoneQ) {
      reordered.push({ ...phoneQ, order: 1, partialSubmit: true })
    }

    if (postcodeQ) {
      reordered.push({ ...postcodeQ, order: 2 })
    }

    if (addressQ) {
      reordered.push({
        ...addressQ,
        order: 3,
        postcodeSource: postcodeQ ? postcodeQ.id : undefined
      })
    }

    otherQuestions.forEach((q, index) => {
      reordered.push({ ...q, order: index + 4 })
    })

    adjustedConfig.questions = reordered
  }

  return adjustedConfig
}
